import { Card } from '@chakra-ui/react';
import {
  IPriceTierSettingsReq,
  IPriceUnderProductSchemaUI,
  IQuoteItemRespSchema,
  IQuoteOfferingRespSchema,
  OfferingTypesEnum,
  PriceTiersEnum,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import {
  formatInteger,
  pluralize,
  sortByProductPosition,
} from '@monetize/utils/core';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../constants/products';
import {
  MBox,
  MButton,
  MCard,
  MCustomIconButton,
  MDivider,
  MFlex,
  MHStack,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../Monetize';
import { SortedProductsWithPricesRow } from './SortedProductsWithPricesRow';

interface ProductWithPriceCardProps {
  id: string;
  prices: IPriceUnderProductSchemaUI['prices'];
  name: string;
  index: number;
  productType: string;
  productsWithPrices: IPriceUnderProductSchemaUI[];
  displayedPriceIds: Record<string, string[]>;
  expandedProducts: Record<string, boolean>;
  isPoTOfferingType: boolean;
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  currency: string;
  handleCheckboxChange: (id: string, priceId: string, checked: boolean) => void;
  isChecked: (id: string, priceId: string) => boolean;
  toggleProduct: (id: string) => void;
  toggleAllTiersForProduct: (
    productId: string,
    prices: { id: string }[],
  ) => void;
  selectFromQuantity: (
    productId: string,
    prices: { id: string; from: number; to: number | null }[],
    quantity: number,
  ) => void;
  setDisplayedPriceIds: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  noMatch: Record<string, boolean>;
  disabled?: boolean;
}

const ProductWithPriceCard = ({
  id,
  prices,
  name,
  index,
  productType,
  productsWithPrices,
  displayedPriceIds,
  expandedProducts,
  isPoTOfferingType,
  productsObj,
  currency,
  handleCheckboxChange,
  isChecked,
  toggleProduct,
  toggleAllTiersForProduct,
  selectFromQuantity,
  setDisplayedPriceIds,
  quoteOffering,
  noMatch,
  setNoMatch,
  disabled,
}: ProductWithPriceCardProps & {
  setNoMatch: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}) => {
  const isLastRow = productsWithPrices.length - 1 === index;
  const selectedCount = displayedPriceIds[id]?.length || 0;
  const formattedCount = formatInteger
    ? formatInteger(selectedCount)
    : selectedCount.toLocaleString();
  const tierLabel = `${formattedCount} ${pluralize('tier', selectedCount)} selected`;
  const hasMultipleTiers = prices.length > 1;

  const quantity = useMemo(() => {
    const item = (quoteOffering?.items || []).find(
      ({ productId }) => productId === id,
    );
    return item?.quantity ?? 0;
  }, [quoteOffering?.items, id]);

  const wrappedHandleCheckboxChange = (
    id: string,
    priceId: string,
    checked: boolean,
  ) => {
    setNoMatch((prev) => ({ ...prev, [id]: false }));
    handleCheckboxChange(id, priceId, checked);
  };
  const wrappedToggleAllTiersForProduct = (
    productId: string,
    prices: { id: string }[],
  ) => {
    setNoMatch((prev) => ({ ...prev, [id]: false }));
    toggleAllTiersForProduct(productId, prices);
  };

  return (
    <Card.Body px={0} py="0">
      <MBox>
        <MFlex
          align="center"
          justifyContent="space-between"
          py="3"
          pr="2"
          role="button"
          onClick={() => toggleProduct(id)}
          cursor={'pointer'}
          _hover={{
            backgroundColor: 'blackAlpha.50',
          }}
        >
          <MBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            pl={2}
          >
            <MBox display="flex" alignItems="center">
              <MCustomIconButton
                btnSize={5}
                variant="ghost"
                icon={expandedProducts[id] ? MdExpandMore : MdChevronRight}
                aria-label="Toggle product prices"
                _hover={{
                  background: 'transparent',
                }}
              />
              <MText as="h5" fontSize="md" fontWeight="bold">
                {name}{' '}
                <MText as="span" fontWeight="normal">
                  ({PRODUCT_TYPE_DISPLAY_SHORT[productType]})
                </MText>
              </MText>
            </MBox>
            {selectedCount > 0 && !expandedProducts[id] && (
              <MText color="tGray.medium" fontSize="sm" ml={2}>
                {tierLabel}
              </MText>
            )}
          </MBox>
        </MFlex>

        {expandedProducts[id] && (
          <>
            {hasMultipleTiers && (
              <MFlex mb={2} pl={6} gap={2} alignItems="center">
                <MButton
                  size="xs"
                  variant="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    wrappedToggleAllTiersForProduct(id, prices);
                  }}
                  isDisabled={disabled}
                >
                  {displayedPriceIds[id]?.length === prices.length
                    ? 'Deselect'
                    : 'Select'}{' '}
                  All
                </MButton>

                {quantity > 0 && hasMultipleTiers && (
                  <MButton
                    size="xs"
                    variant="tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectFromQuantity(id, prices, quantity);
                    }}
                    isDisabled={disabled}
                  >
                    Select from quote quantity
                  </MButton>
                )}

                {noMatch[id] && (
                  <MText color="red.500" fontSize="xs" ml={2}>
                    No matched tiers
                  </MText>
                )}
              </MFlex>
            )}
            <MHStack gap={4} mb="2" pl={6}>
              <MBox minW="3" />
              <MText minW="36" fontWeight="bold">
                Pricing Model
              </MText>
              <MText minW="6.25rem" fontWeight="bold">
                From
              </MText>
              <MText minW="6.25rem" fontWeight="bold">
                Up to
              </MText>
              <MText minW="32" fontWeight="bold">
                {isPoTOfferingType ? 'Percentage' : 'Amount'}
              </MText>
              <MBox minW="8" />
            </MHStack>
            <MBox pl={6}>
              <SortedProductsWithPricesRow
                prices={prices}
                id={id}
                isPoTOfferingType={isPoTOfferingType}
                productsObj={productsObj}
                currency={currency}
                handleCheckboxChange={wrappedHandleCheckboxChange}
                isChecked={isChecked}
                onSelectFromTier={(tierIndex) => {
                  setDisplayedPriceIds((prev) => ({
                    ...prev,
                    [id]: prices.slice(tierIndex).map((p) => p.id),
                  }));
                  setNoMatch((prev) => ({ ...prev, [id]: false }));
                }}
                hasMultipleTiers={hasMultipleTiers}
                disabled={disabled}
              />
            </MBox>
          </>
        )}
      </MBox>
      {!isLastRow && <MDivider my="2" />}
    </Card.Body>
  );
};

interface PricesDrawerBodyViewProps {
  isSaving: boolean;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  offeringType: OfferingTypesEnum;
  productsWithPrices: IPriceUnderProductSchemaUI[];
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  currency: string;
  isPriceTierUpdated: boolean;
  savePriceTiers: ({
    priceDisplayMode,
    displayedPriceIds,
  }: IPriceTierSettingsReq) => void;
  setIsPriceTierUpdated: (val: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  disabled?: boolean;
}

const NUMBER_OF_ITEMS_TO_COLLAPSE = 20;

export const QuotePriceTierDisplaySettings = ({
  isSaving,
  quoteOffering,
  productsWithPrices,
  productsObj,
  offeringType,
  currency,
  savePriceTiers,
  isPriceTierUpdated,
  setIsPriceTierUpdated,
  setIsLoading,
  setIsSaving,
  disabled,
}: PricesDrawerBodyViewProps) => {
  const [expandedProducts, setExpandedProducts] = useState<
    Record<string, boolean>
  >({});
  const [isPending, startTransition] = useTransition();
  const isPoTOfferingType =
    offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;
  const didAutoExpand = useRef(false);

  const [tiersValue, setTiersValue] = useState<PriceTiersEnum>(
    (quoteOffering?.items[0]?.options?.priceDisplayMode as PriceTiersEnum) ??
      PriceTiersEnum.ALL_PRICES,
  );

  const [displayedPriceIds, setDisplayedPriceIds] = useState<
    Record<string, string[]>
  >({});

  const isQuoteOfferingLocked = quoteOffering?.locked || false;

  const computedDisplayedPriceIds = useMemo(
    () =>
      quoteOffering?.items.reduce(
        (acc, { productId, options }) => {
          acc[productId] = options?.displayedPriceIds ?? [];
          return acc;
        },
        {} as Record<string, string[]>,
      ) ?? {},
    [quoteOffering?.items],
  );

  const allPricesSelected = useMemo(() => {
    return productsWithPrices.every(({ id, prices }) => {
      return displayedPriceIds[id]?.length === prices.length;
    });
  }, [displayedPriceIds, productsWithPrices]);

  useEffect(() => {
    if (quoteOffering?.items && tiersValue === PriceTiersEnum.CUSTOM_PRICES) {
      quoteOffering.items.forEach((item) => {
        setDisplayedPriceIds((prev) => {
          return {
            ...prev,
            [item.productId]: item.options?.displayedPriceIds ?? [],
          };
        });
      });
    }
  }, [quoteOffering?.items, tiersValue]);

  const lastServerState = useRef<string | null>(null);

  useEffect(() => {
    if (quoteOffering?.items) {
      const currentServerState = JSON.stringify({
        priceDisplayMode: quoteOffering.items[0]?.options?.priceDisplayMode,
        displayedPriceIds: quoteOffering.items.reduce(
          (acc, item) => {
            acc[item.productId] = item.options?.displayedPriceIds ?? [];
            return acc;
          },
          {} as Record<string, string[]>,
        ),
      });

      if (lastServerState.current !== currentServerState) {
        const serverTierValue =
          (quoteOffering.items[0]?.options
            ?.priceDisplayMode as PriceTiersEnum) ?? PriceTiersEnum.ALL_PRICES;
        setTiersValue(serverTierValue);

        if (serverTierValue === PriceTiersEnum.CUSTOM_PRICES) {
          const serverDisplayedPriceIds = quoteOffering.items.reduce(
            (acc, item) => {
              acc[item.productId] = item.options?.displayedPriceIds ?? [];
              return acc;
            },
            {} as Record<string, string[]>,
          );
          setDisplayedPriceIds(serverDisplayedPriceIds);
        }

        setIsPriceTierUpdated(false);
        lastServerState.current = currentServerState;
      }
    }
  }, [quoteOffering?.items, setIsPriceTierUpdated]);

  useEffect(() => {
    const priceDisplayMode = quoteOffering?.items[0]?.options
      ?.priceDisplayMode as PriceTiersEnum;
    const isTierUpdated = tiersValue !== priceDisplayMode;

    if (tiersValue === PriceTiersEnum.CUSTOM_PRICES) {
      setIsPriceTierUpdated(
        JSON.stringify(displayedPriceIds) !==
          JSON.stringify(computedDisplayedPriceIds),
      );
    } else {
      setIsPriceTierUpdated(isTierUpdated);
    }
  }, [
    tiersValue,
    quoteOffering?.items,
    displayedPriceIds,
    computedDisplayedPriceIds,
    setIsPriceTierUpdated,
  ]);

  const handleCheckboxChange = useCallback(
    (id: string, priceId: string, checked: boolean) => {
      setDisplayedPriceIds((prev) => {
        const updatedPrices = new Set(prev[id] ?? []);

        if (checked) {
          updatedPrices.add(priceId);
        } else {
          updatedPrices.delete(priceId);
        }

        return { ...prev, [id]: Array.from(updatedPrices) };
      });
    },
    [],
  );

  const isChecked = useMemo(
    () => (id: string, priceId: string) =>
      displayedPriceIds[id]?.includes(priceId) ?? false,
    [displayedPriceIds],
  );

  const isSubmitDisabled = useMemo(() => {
    const hasDisplayedPrices = Object.values(displayedPriceIds).some(
      (prices) => prices.length > 0,
    );

    if (tiersValue === PriceTiersEnum.CUSTOM_PRICES) {
      return !isPriceTierUpdated || !hasDisplayedPrices;
    }

    return !isPriceTierUpdated;
  }, [displayedPriceIds, tiersValue, isPriceTierUpdated]);

  const sortedProductsWithPrices = sortByProductPosition(
    productsWithPrices.map((item) => ({
      ...item,
      position: productsObj.byId[item.id]?.position ?? 0,
    })),
  );

  useEffect(() => {
    if (!didAutoExpand.current) {
      const totalItems = productsWithPrices.reduce((acc, item) => {
        return acc + item.prices.length;
      }, 0);
      if (totalItems < NUMBER_OF_ITEMS_TO_COLLAPSE) {
        setExpandedProducts(
          productsWithPrices.reduce(
            (acc, item) => {
              acc[item.id] = true;
              return acc;
            },
            {} as Record<string, boolean>,
          ),
        );
      }
      didAutoExpand.current = true;
    }
  }, []);

  const handlePriceTiersSubmit = async () => {
    setIsSaving(true);
    try {
      await savePriceTiers({
        priceDisplayMode: tiersValue,
        displayedPriceIds,
      });
      setIsPriceTierUpdated(false);
    } finally {
      setIsSaving(false);
    }
  };

  const updateAllPricesSelection = () => {
    setDisplayedPriceIds((prev) => {
      if (allPricesSelected) {
        return {}; // Reset state
      }

      return productsWithPrices.reduce(
        (acc, { id, prices }) => {
          acc[id] = prices.map(({ id }) => id);
          return acc;
        },
        {} as Record<string, string[]>,
      );
    });
  };

  const resetPricesSelection = () => {
    setDisplayedPriceIds({});
  };

  const toggleProduct = (productId: string) => {
    if (expandedProducts[productId]) {
      setExpandedProducts((prev) => ({
        ...prev,
        [productId]: false,
      }));
    } else {
      startTransition(() => {
        setExpandedProducts((prev) => ({
          ...prev,
          [productId]: true,
        }));
      });
    }
  };

  const toggleAllTiersForProduct = (
    productId: string,
    prices: { id: string }[],
  ) => {
    setDisplayedPriceIds((prev) => {
      const allTierIds = prices.map((p) => p.id);
      const allSelected = prev[productId]?.length === allTierIds.length;
      return {
        ...prev,
        [productId]: allSelected ? [] : allTierIds,
      };
    });
  };

  const [noMatch, setNoMatch] = useState<Record<string, boolean>>({});

  const selectFromQuantity = (
    productId: string,
    prices: { id: string; from: number; to: number | null }[],
    quantity: number,
  ) => {
    const startIdx = prices.findIndex(
      (tier) =>
        quantity >= tier.from && (tier.to === null || quantity <= tier.to),
    );
    if (startIdx === -1) {
      setNoMatch((prev) => ({ ...prev, [productId]: true }));
      return;
    }
    setNoMatch((prev) => ({ ...prev, [productId]: false }));
    setDisplayedPriceIds((prev) => ({
      ...prev,
      [productId]: prices.slice(startIdx).map((p) => p.id),
    }));
  };

  return (
    <MCard variant="borderless" mb="2">
      <Card.Body>
        <MText as="h5" fontSize="md" fontWeight="bold" truncate>
          Show Price Tiers
        </MText>

        <MDivider my={4} />
        <MBox
          height="calc(100vh - 360px)"
          overflowY="auto"
          overflowX="hidden"
          px={1}
        >
          <MText mb={4}>
            Customize which pricing tiers are shared to the end customer on the
            Quote:
          </MText>
          <MRadioGroup
            value={tiersValue}
            onChange={(value) => {
              setTiersValue(value as PriceTiersEnum);
              if (value !== PriceTiersEnum.CUSTOM_PRICES) {
                resetPricesSelection();
              }
            }}
          >
            <MStack gap={4} direction="column" color="tGray.darkPurple">
              <MRadio
                value={PriceTiersEnum.ALL_PRICES}
                cursor={isSaving ? 'not-allowed' : 'pointer'}
                isDisabled={isSaving || disabled || isQuoteOfferingLocked}
              >
                Show All Tiers
              </MRadio>
              <MRadio
                value={PriceTiersEnum.USED_PRICES}
                cursor={isSaving ? 'not-allowed' : 'pointer'}
                isDisabled={isSaving || disabled || isQuoteOfferingLocked}
              >
                Show used Tiers
              </MRadio>
              <MRadio
                value={PriceTiersEnum.CUSTOM_PRICES}
                cursor={isSaving || disabled ? 'not-allowed' : 'pointer'}
                isDisabled={isSaving || disabled || isQuoteOfferingLocked}
              >
                Customize Visible Tiers
              </MRadio>
            </MStack>
          </MRadioGroup>

          {tiersValue === PriceTiersEnum.CUSTOM_PRICES && (
            <>
              <MFlex mt={4}>
                <MButton
                  size={'sm'}
                  variant="tertiary"
                  onClick={updateAllPricesSelection}
                  isDisabled={disabled}
                >
                  {allPricesSelected ? 'Deselect' : 'Select'} All
                </MButton>
              </MFlex>
              {sortedProductsWithPrices.map(
                ({ id, prices, name, productType }, index) => {
                  return (
                    <ProductWithPriceCard
                      key={index}
                      id={id}
                      prices={prices}
                      name={name}
                      index={index}
                      productType={productType}
                      productsWithPrices={productsWithPrices}
                      displayedPriceIds={displayedPriceIds}
                      expandedProducts={expandedProducts}
                      isPoTOfferingType={isPoTOfferingType}
                      productsObj={productsObj}
                      currency={currency}
                      handleCheckboxChange={handleCheckboxChange}
                      isChecked={isChecked}
                      toggleProduct={toggleProduct}
                      toggleAllTiersForProduct={toggleAllTiersForProduct}
                      selectFromQuantity={selectFromQuantity}
                      setDisplayedPriceIds={setDisplayedPriceIds}
                      quoteOffering={quoteOffering}
                      noMatch={noMatch}
                      setNoMatch={setNoMatch}
                      disabled={disabled || isQuoteOfferingLocked}
                    />
                  );
                },
              )}
            </>
          )}
        </MBox>
        <MDivider my={4} />
        <MFlex
          justifyContent="center"
          align="center"
          position="sticky"
          bottom="0"
          background="tWhite.base"
          padding="12px"
          borderTop="1px solid #e2e8f0"
          zIndex="10"
        >
          <MButton
            isLoading={isSaving}
            onClick={handlePriceTiersSubmit}
            disabled={isSubmitDisabled || isSaving}
          >
            Save
          </MButton>
        </MFlex>
      </Card.Body>
    </MCard>
  );
};
